import React, { useState, useEffect } from 'react';
import { Button } from '@components/common/button';
import { Input } from '@components/common/input';
import { Modal } from '@components/common/modal';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  CreditCard,
  Building2,
  UserCircle
} from 'lucide-react';
import { Client, CreateClientPayload } from '@/types/client_types';

interface ClientFormProps {
  is_open: boolean;
  on_close: () => void;
  on_submit: (data: CreateClientPayload) => void;
  initial_data?: Client;
  is_loading?: boolean;
  mode?: 'create' | 'edit';
}

export const ClientForm: React.FC<ClientFormProps> = ({
  is_open,
  on_close,
  on_submit,
  initial_data,
  is_loading = false,
  mode = 'create'
}) => {
  const [form_data, set_form_data] = useState<CreateClientPayload>({
    first_name: '',
    last_name: '',
    client_type: 'TENANT',
    email: '',
    phone_number: '',
    id_number: '',
    physical_address: '',
    city: '',
    county: '',
    postal_code: '',
    is_active: true
  });
  
  const [errors, set_errors] = useState<Record<string, string>>({});
  
  // Populate form when editing
  useEffect(() => {
    if (initial_data && mode === 'edit') {
      set_form_data({
        first_name: initial_data.first_name || '',
        last_name: initial_data.last_name || '',
        client_type: initial_data.client_type,
        email: initial_data.email || '',
        phone_number: initial_data.phone_number || '',
        id_number: initial_data.id_number || '',
        physical_address: initial_data.physical_address || '',
        city: initial_data.city || '',
        county: initial_data.county || '',
        postal_code: initial_data.postal_code || '',
        is_active: initial_data.is_active
      });
    } else {
      // Reset form for create mode
      set_form_data({
        first_name: '',
        last_name: '',
        client_type: 'TENANT',
        email: '',
        phone_number: '',
        id_number: '',
        physical_address: '',
        city: '',
        county: '',
        postal_code: '',
        is_active: true
      });
    }
    set_errors({});
  }, [initial_data, mode, is_open]);
  
  const validate_form = (): boolean => {
    const new_errors: Record<string, string> = {};
    
    if (!form_data.first_name?.trim()) {
      new_errors.first_name = 'First name is required';
    }
    
    if (!form_data.last_name?.trim()) {
      new_errors.last_name = 'Last name is required';
    }
    
    if (!form_data.phone_number?.trim() && !form_data.email?.trim()) {
      new_errors.contact = 'Either phone or email is required';
    }
    
    if (form_data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form_data.email)) {
      new_errors.email = 'Invalid email format';
    }
    
    if (form_data.phone_number && !/^[0-9+\-\s()]+$/.test(form_data.phone_number)) {
      new_errors.phone_number = 'Invalid phone format';
    }
    
    if (!form_data.physical_address?.trim()) {
      new_errors.physical_address = 'Physical address is required';
    }
    
    if (!form_data.city?.trim()) {
      new_errors.city = 'City is required';
    }
    
    if (!form_data.county?.trim()) {
      new_errors.county = 'County is required';
    }
    
    set_errors(new_errors);
    return Object.keys(new_errors).length === 0;
  };
  
  const handle_submit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate_form()) {
      on_submit(form_data);
    }
  };
  
  const handle_change = (field: keyof CreateClientPayload, value: any) => {
    set_form_data(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      set_errors(prev => {
        const new_errors = { ...prev };
        delete new_errors[field];
        return new_errors;
      });
    }
  };
  
  const handle_close = () => {
    if (!is_loading) {
      set_form_data({
        first_name: '',
        last_name: '',
        client_type: 'TENANT',
        email: '',
        phone_number: '',
        id_number: '',
        physical_address: '',
        city: '',
        county: '',
        postal_code: '',
        is_active: true
      });
      set_errors({});
      on_close();
    }
  };
  
  return (
    <Modal
      is_open={is_open}
      on_close={handle_close}
      title={mode === 'create' ? 'Add New Client' : 'Edit Client'}
      size="lg"
      footer={
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={handle_close}
            disabled={is_loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handle_submit}
            is_loading={is_loading}
          >
            {mode === 'create' ? 'Create Client' : 'Update Client'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handle_submit} className="space-y-6">
        {/* Client Type Selection */}
        <div>
          <label className="block text-sm font-medium text-charcoal mb-3">
            Client Type <span className="text-error">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handle_change('client_type', 'TENANT')}
              className={`p-4 border-2 rounded-lg transition-smooth ${
                form_data.client_type === 'TENANT'
                  ? 'border-forest-green bg-forest-green bg-opacity-5'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <UserCircle 
                size={32} 
                className={`mx-auto mb-2 ${
                  form_data.client_type === 'TENANT' 
                    ? 'text-forest-green' 
                    : 'text-gray-400'
                }`}
              />
              <p className={`font-medium ${
                form_data.client_type === 'TENANT' 
                  ? 'text-forest-green' 
                  : 'text-gray-700'
              }`}>
                Tenant
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Direct water consumer
              </p>
            </button>
            
            <button
              type="button"
              onClick={() => handle_change('client_type', 'LANDLORD')}
              className={`p-4 border-2 rounded-lg transition-smooth ${
                form_data.client_type === 'LANDLORD'
                  ? 'border-forest-green bg-forest-green bg-opacity-5'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <Building2 
                size={32} 
                className={`mx-auto mb-2 ${
                  form_data.client_type === 'LANDLORD' 
                    ? 'text-forest-green' 
                    : 'text-gray-400'
                }`}
              />
              <p className={`font-medium ${
                form_data.client_type === 'LANDLORD' 
                  ? 'text-forest-green' 
                  : 'text-gray-700'
              }`}>
                Landlord
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Property owner with units
              </p>
            </button>
          </div>
        </div>
        
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Basic Information
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              placeholder="Enter first name"
              value={form_data.first_name}
              onChange={(e) => handle_change('first_name', e.target.value)}
              icon_left={<User size={18} />}
              error={errors.first_name}
              is_required
            />
            
            <Input
              label="Last Name"
              placeholder="Enter last name"
              value={form_data.last_name}
              onChange={(e) => handle_change('last_name', e.target.value)}
              icon_left={<User size={18} />}
              error={errors.last_name}
              is_required
            />
          </div>
          
          <Input
            label="ID Number"
            placeholder="National ID or Passport Number"
            value={form_data.id_number}
            onChange={(e) => handle_change('id_number', e.target.value)}
            icon_left={<CreditCard size={18} />}
            error={errors.id_number}
            helper_text="National ID or Passport Number"
            is_required
          />
        </div>
        
        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Contact Information
          </h3>
          
          {errors.contact && (
            <div className="p-3 bg-error bg-opacity-10 border border-error rounded-lg">
              <p className="text-sm text-error">{errors.contact}</p>
            </div>
          )}
          
          <Input
            label="Email Address"
            type="email"
            placeholder="client@example.com"
            value={form_data.email}
            onChange={(e) => handle_change('email', e.target.value)}
            icon_left={<Mail size={18} />}
            error={errors.email}
            helper_text="Used for invoice delivery"
          />
          
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+254 712 345 678"
            value={form_data.phone_number}
            onChange={(e) => handle_change('phone_number', e.target.value)}
            icon_left={<Phone size={18} />}
            error={errors.phone_number}
            helper_text="Used for SMS notifications"
          />
        </div>
        
        {/* Location */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Location
          </h3>
          
          <Input
            label="Physical Address"
            placeholder="Street, Building, Area"
            value={form_data.physical_address}
            onChange={(e) => handle_change('physical_address', e.target.value)}
            icon_left={<MapPin size={18} />}
            error={errors.physical_address}
            helper_text="Complete physical address for property location"
            is_required
          />
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City"
              placeholder="e.g., Nairobi"
              value={form_data.city}
              onChange={(e) => handle_change('city', e.target.value)}
              icon_left={<MapPin size={18} />}
              error={errors.city}
              is_required
            />
            
            <Input
              label="County"
              placeholder="e.g., Nairobi County"
              value={form_data.county}
              onChange={(e) => handle_change('county', e.target.value)}
              icon_left={<MapPin size={18} />}
              error={errors.county}
              is_required
            />
          </div>
          
          <Input
            label="Postal Code"
            placeholder="e.g., 00100"
            value={form_data.postal_code}
            onChange={(e) => handle_change('postal_code', e.target.value)}
            icon_left={<MapPin size={18} />}
            helper_text="Optional"
          />
        </div>
        
        {/* Status */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Account Status
          </h3>
          
          <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-smooth">
            <input
              type="checkbox"
              checked={form_data.is_active}
              onChange={(e) => handle_change('is_active', e.target.checked)}
              className="w-5 h-5 text-forest-green border-gray-300 rounded focus:ring-forest-green focus:ring-2"
            />
            <div>
              <p className="font-medium text-charcoal">Active Account</p>
              <p className="text-xs text-gray-500">
                Client can receive invoices and make payments
              </p>
            </div>
          </label>
        </div>
        
        {/* Info Message for Landlords */}
        {form_data.client_type === 'LANDLORD' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> After creating this landlord account, you can add properties and units to manage multiple tenants.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
};